import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { ProfileResponse } from '@chess-uno/shared-protocol'
import { fetchProfile, shareText } from '../api/competition.js'
import type { StoredSession } from '../api/session.js'
import { FormDots, RatingHero, RecentMatches, RivalsList, ScoreBar, SkeletonList, taunt, useChallenge } from '../components/Competition.js'
import { Avatar, Icon, TopBar } from '../components/Chrome.js'
import { useI18n } from '../i18n/index.js'

/** A player's public profile: rating, records, latest results. Shareable link. */
export function ProfilePage({ session }: { session: StoredSession | null }) {
  const { username = '' } = useParams()
  const [profile, setProfile] = useState<ProfileResponse | null | undefined>(undefined)
  const [shared, setShared] = useState<string | null>(null)
  const { challenge, error } = useChallenge(session)
  const { t, path } = useI18n()

  useEffect(() => {
    setProfile(undefined)
    void fetchProfile(username, session).then(setProfile)
  }, [username, session])

  if (profile === undefined)
    return (
      <div className="app-shell">
        <TopBar session={session} />
        <main className="profile-page" aria-busy="true">
          <div className="rating-hero skeleton" aria-hidden="true" />
          <div className="dash-grid even">
            <section className="card">
              <SkeletonList rows={3} />
            </section>
            <section className="card">
              <SkeletonList rows={4} avatar={false} />
            </section>
          </div>
        </main>
      </div>
    )
  if (profile === null)
    return (
      <div className="app-shell">
        <TopBar session={session} />
        <main className="narrow-page">
          <p className="error">{t.profile.notFound}</p>
          <Link to={path('/')} className="back-link">
            <Icon name="arrow-left" size={14} /> {t.common.home}
          </Link>
        </main>
      </div>
    )

  const p = profile.player
  const isMe = session?.playerId === p.playerId
  const vs = profile.vsViewer

  return (
    <div className="app-shell">
    <TopBar session={session} />
    <main className="profile-page" data-testid="profile">
      <Link to={path('/')} className="back-link">
        <Icon name="arrow-left" size={14} /> {t.common.home}
      </Link>
      <header className="profile-header">
        <Avatar name={p.name} size={56} />
        <div>
          <p className="eyebrow">{t.profile.eyebrow}</p>
          <h1>{p.name}</h1>
        </div>
        <div className="profile-actions">
          {session && !isMe && (
            <button type="button" className="primary" onClick={() => void challenge(p.name)} data-testid="profile-challenge">
              {t.profile.challenge(p.name)}
            </button>
          )}
          <button
            type="button"
            className="ghost"
            onClick={async () => {
              const text = isMe ? t.profile.shareMe(p.rank, p.rating) : t.profile.shareOther(p.name, p.rating)
              const r = await shareText(text, window.location.href)
              setShared(r === 'copied' ? t.profile.linkCopied : r === 'shared' ? t.profile.shared : null)
            }}
          >
            <Icon name="share" size={14} /> {t.profile.share}
          </button>
          {shared && <span className="fine">{shared}</span>}
        </div>
      </header>
      {error && <p className="error">{error}</p>}

      <RatingHero entry={p} rankedPlayers={profile.rankedPlayers} eyebrow={isMe ? t.profile.yourRanking : t.profile.ranking} />

      {vs && (
        <section className="card vs-viewer" data-testid="vs-viewer">
          <p className="eyebrow">{t.profile.youVs(p.name)}</p>
          <p className="big-score">
            {vs.wins} <i>–</i> {vs.losses}
            {vs.draws > 0 && <small> · {t.profile.draws(vs.draws)}</small>}
          </p>
          <ScoreBar wins={vs.wins} draws={vs.draws} losses={vs.losses} />
          <p className="vs-foot">
            <FormDots form={vs.form} /> <span className="muted">{taunt(vs, t)}</span>
          </p>
        </section>
      )}

      <div className="dash-grid even">
        <section className="card">
          <header className="card-head">
            <h2>{t.profile.headToHead}</h2>
          </header>
          <RivalsList rivals={profile.rivals} taunts={isMe} />
        </section>
        <section className="card">
          <header className="card-head">
            <h2>{t.profile.recentResults}</h2>
          </header>
          <RecentMatches matches={profile.recent} perspective={p.playerId} />
        </section>
      </div>
    </main>
    </div>
  )
}
